import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import { adminApiRequest } from "../utils/adminApi";
import { useToast } from "../utils/ToastContext";

const FullFormDetails = ({ rental }) => {
  return (
    <div className="space-y-2 text-sm">
      {Object.entries(rental)
        .filter(([key]) => key !== "id")
        .map(([key, value]) => {
          let displayValue;

          if (key === "supporting_docs" && value) {
            displayValue = (
              <a
                href={value}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                View File
              </a>
            );
          } else if (key === "agreed_terms") {
            displayValue = value === "1" || value === 1 ? "Yes" : "No";
          } else {
            displayValue = value || "—";
          }

          return (
            <div key={key} className="flex gap-2">
              <strong className="capitalize">
                {key.replace(/_/g, " ")}:
              </strong>
              <span>{displayValue}</span>
            </div>
          );
        })}
    </div>
  );
};

const HallRentals = () => {
  const { showToast } = useToast();
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [modalConfig, setModalConfig] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [activeRental, setActiveRental] = useState(null);
  const [isSelectingText, setIsSelectingText] = useState(false);

  const fetchRentals = async () => {
    setLoading(true);

    try {
      const data = await adminApiRequest({ script: "get-hall-rentals" });

      if (data.success) {
        const sortedRentals = data.data.sort(
          (a, b) =>
            new Date(a.date_submitted) - new Date(b.date_submitted)
        );

        setRentals(sortedRentals);
      }
    } catch (err) {
      console.error("Error fetching rentals:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRentals();
  }, []);

  const handleDelete = async (id) => {
    setActionLoading(true);

    try {
      const data = await adminApiRequest({
        script: "delete-hall-rental",
        data: { id },
      });

      if (data.success) {
        fetchRentals();
        closeModal();
      } else {
        showToast("Delete failed: " + data.message);
      }
    } catch (err) {
      console.error(err);
      showToast("Delete error.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!activeRental) return;

    setActionLoading(true);

    try {
      const data = await adminApiRequest({
        script: "update-hall-notes",
        data: { id: activeRental.id, notes: noteText },
      });

      if (data.success) {
        setRentals((prev) =>
          prev.map((r) =>
            r.id === activeRental.id ? { ...r, notes: noteText } : r
          )
        );
      } else {
        showToast("Failed to save notes: " + data.message);
      }
    } catch (err) {
      console.error(err);
      showToast("Error saving notes.");
    } finally {
      setActionLoading(false);
      closeModal();
    }
  };

  const openConfirmModal = (title, onConfirm, content) => {
    setModalConfig({ title, onConfirm, content, type: "CONFIRM_MODAL" });
  };

  const openNotesModal = (rental) => {
    setActiveRental(rental);
    setNoteText(rental.notes || "");

    setModalConfig({
      title: `Notes for ${rental.full_name}`,
      content: "NOTES_MODAL",
    });

  };

  const openFullDetailsModal = (rental) => {
    setModalConfig({
      content: <FullFormDetails rental={rental} />,
      type: "DETAIL_MODAL",
    });

  };

  const closeModal = () => {
    setModalConfig(null);
    setActiveRental(null);
  };

  return (
    <div className="h-full flex flex-col p-2 overflow-hidden">

      {/* Header */}

      <div className="flex items-center justify-between mb-5 flex-shrink-0">
        <h1 className="text-2xl font-semibold text-slate-800 tracking-tight">
          Hall Rentals
        </h1>
      </div>

      {(loading || actionLoading) && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="w-10 h-10 border-4 border-slate-300 border-t-slate-900 rounded-full animate-spin"></div>
        </div>
      )}

      {!loading && rentals.length === 0 ? (
        <div className="text-sm text-slate-400">
          No records found
        </div>
      ) : (
        <div className="flex-1 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">

          <div className="h-full overflow-auto">

            <table className="min-w-[1600px] text-sm">

              <thead className="sticky top-0 bg-slate-100 z-20 border-b border-slate-200">

                <tr className="text-slate-600 text-xs uppercase tracking-wide">

                  <th className="sticky left-0 bg-slate-100 px-4 py-3">#</th>

                  <th className="px-4 py-3">Full Name</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Street</th>
                  <th className="px-4 py-3">Country</th>
                  <th className="px-4 py-3">City</th>
                  <th className="px-4 py-3">Postcode</th>
                  <th className="px-4 py-3">Event</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Start</th>
                  <th className="px-4 py-3">End</th>
                  <th className="px-4 py-3">Guests</th>
                  <th className="px-4 py-3">Requirements</th>
                  <th className="px-4 py-3">Docs</th>
                  <th className="px-4 py-3">Terms</th>
                  <th className="px-4 py-3">Signature</th>
                  <th className="px-4 py-3">Submitted</th>
                  <th className="px-4 py-3">Notes</th>
                  <th className="px-4 py-3 text-right">Actions</th>

                </tr>

              </thead>

              <tbody>

                {rentals.map((rental, index) => (

                  <tr
                    key={rental.id}
                    className="border-b border-slate-100 hover:bg-slate-50 transition"
                    onMouseDown={() => setIsSelectingText(false)}
                    onMouseMove={() => setIsSelectingText(true)}
                    onMouseUp={(e) => {
                      if (
                        !isSelectingText &&
                        !["BUTTON", "A", "I", "TEXTAREA"].includes(
                          e.target.tagName
                        )
                      ) {
                        openFullDetailsModal(rental);
                      }
                    }}
                  >

                    <td className="sticky left-0 bg-white px-4 py-3 text-slate-500">
                      {index + 1}
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      {rental.full_name}
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      {rental.contact_number}
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      {rental.email}
                    </td>

                    <td className="px-4 py-3 max-w-[200px] truncate">
                      {rental.street_address}
                    </td>

                    <td className="px-4 py-3">{rental.country}</td>
                    <td className="px-4 py-3">{rental.city}</td>
                    <td className="px-4 py-3">{rental.postcode}</td>

                    <td className="px-4 py-3">{rental.event_type}</td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      {new Date(
                        rental.event_date
                      ).toLocaleDateString("en-GB")}
                    </td>

                    <td className="px-4 py-3">{rental.start_time}</td>
                    <td className="px-4 py-3">{rental.end_time}</td>

                    <td className="px-4 py-3">{rental.guests}</td>

                    <td className="px-4 py-3 max-w-[220px] truncate">
                      {rental.special_requirements || "—"}
                    </td>

                    <td className="px-4 py-3">

                      {rental.supporting_docs ? (

                        <a
                          href={rental.supporting_docs}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          View
                        </a>

                      ) : "None"}

                    </td>

                    <td className="px-4 py-3">
                      {rental.agreed_terms === "1" ||
                      rental.agreed_terms === 1
                        ? "Yes"
                        : "No"}
                    </td>

                    <td className="px-4 py-3">
                      {rental.signature || "—"}
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      {new Date(
                        rental.date_submitted
                      ).toLocaleDateString("en-GB")}
                    </td>

                    <td className="px-4 py-3 max-w-[200px] truncate">
                      {rental.notes || "—"}
                    </td>

                    <td
                      className="px-4 py-3"
                      onClick={(e) => e.stopPropagation()}
                    >

                      <div className="flex justify-end gap-2">

                        <button
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition hover:scale-105 active:scale-95"
                          onClick={() => openNotesModal(rental)}
                          title="Notes"
                        >
                          <i className="fa fa-sticky-note text-xs"></i>
                        </button>

                        <button
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition hover:scale-105 active:scale-95"
                          onClick={() =>
                            (window.location.href = `mailto:${rental.email}`)
                          }
                        >
                          <i className="fa fa-envelope text-xs"></i>
                        </button>

                        <button
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-green-600 text-white hover:bg-green-700 transition hover:scale-105 active:scale-95"
                          onClick={() =>
                            (window.location.href = `tel:${rental.contact_number}`)
                          }
                        >
                          <i className="fa fa-phone text-xs"></i>
                        </button>

                        <button
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-600 text-white hover:bg-red-700 transition hover:scale-105 active:scale-95"
                          onClick={() =>
                            openConfirmModal(
                              "Delete Hall Rental",
                              () => handleDelete(rental.id),
                              <p>
                                Delete hall rental for{" "}
                                <strong>{rental.full_name}</strong>?
                              </p>
                            )
                          }
                        >
                          <i className="fa fa-trash text-xs"></i>
                        </button>

                      </div>

                    </td>

                  </tr>

                ))}

              </tbody>

            </table>

          </div>

        </div>
      )}

      {modalConfig && (
        <Modal
          title={modalConfig.title}
          onClose={closeModal}
          showCancel={modalConfig.type === "CONFIRM_MODAL"}
          showSubmitButton={modalConfig.type === "CONFIRM_MODAL"}
          submitButtonText="Delete"
          onSubmit={async (e) => {
            e.preventDefault();
            if (modalConfig.onConfirm) await modalConfig.onConfirm();
          }}
        >
          {modalConfig.content === "NOTES_MODAL" ? (
            <div className="space-y-4">

              <textarea
                className="w-full border rounded-lg p-3 text-sm"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
              />

              <div className="flex justify-end gap-2">

                <button
                  className="px-4 py-2 rounded bg-slate-200"
                  onClick={closeModal}
                >
                  Cancel
                </button>

                <button
                  className="px-4 py-2 rounded bg-indigo-600 text-white"
                  onClick={handleSaveNotes}
                >
                  Save
                </button>

              </div>

            </div>
          ) : (
            modalConfig.content
          )}
        </Modal>
      )}

    </div>
  );
};

export default HallRentals;